import {
  ApButtonIcon,
  ApButtonMain,
  ApButtonSecondary,
  ApIcon,
  ApSearchDropdown,
  ApTooltip,
} from '@alixpartners/ui-components';
import * as React from 'react';
import { IIndustryBlend } from 'utils/types';
import {
  isValidLocalBlend,
  blendAPIToLocal,
  getYearInputHint,
  REVENUE_UPLIFT_INPUT_HINT,
} from './Benchmarks.helpers';
import {
  BlendDataText,
  ConfigurationRow,
  EditContainer,
  IndustryFormActionRowBetween,
  IndustryNameWrapper,
  InputLabel,
  SelectContainer,
  SelectYearDescription,
  TooltipContent,
} from './Benchmarks.styles';
import { DeleteBlendConfirmationModal } from './DeleteBlendConfirmation';

export const ExistingIndustryBlend = ({
  availableYears,
  calculateBlend,
  activatedBlend,
  industryBlends,
  isCreateBlend,
  isEditBlend,
  selectedBlend,
  onAddBlend,
  onEditIndustryBlend,
  onDeleteBlend,
  updateAndApplyBlend,
  searchBlendInputValue,
  setSelectedBlend,
  hasWritePermission,
}: {
  activatedBlend?: IIndustryBlend;
  selectedBlend?: IIndustryBlend;
  availableYears: number[];
  calculateBlend: (o: IIndustryBlend) => void;
  industryBlends: IIndustryBlend[];
  isCreateBlend: boolean;
  isEditBlend: boolean;
  onAddBlend: () => void;
  onEditIndustryBlend: (o: IIndustryBlend) => void;
  onDeleteBlend: (o: IIndustryBlend) => void;
  updateAndApplyBlend: (o: IIndustryBlend) => void;
  searchBlendInputValue: string;
  setSelectedBlend: (o: IIndustryBlend) => void;
  hasWritePermission: boolean;
}) => {
  const industryBlendOptions = industryBlends.map((blend) => {
    return {
      name: `${blend.name}${blend.isActive ? ' (currently applied)' : ''}`,
      value: blend.id || 0,
    };
  });
  const blend = selectedBlend || activatedBlend;
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    React.useState<boolean>(false);

  const isApplyEnabled = !blend?.isActive && selectedBlend;
  return (
    <div>
      <ConfigurationRow>
        <IndustryNameWrapper>
          <InputLabel>Industry blend</InputLabel>
          <ApSearchDropdown
            options={industryBlendOptions}
            disabled={isCreateBlend || isEditBlend || !hasWritePermission}
            placeholder="Select or type"
            onSuggestionSelected={(blend) => {
              const selectedBlendObject = industryBlends.find(
                (opt) => opt.id === blend
              );
              if (selectedBlendObject) {
                setSelectedBlend(selectedBlendObject);
              }
              if (
                selectedBlendObject &&
                isValidLocalBlend(blendAPIToLocal(selectedBlendObject))
              ) {
                calculateBlend(selectedBlendObject);
              }
            }}
            value={searchBlendInputValue}
          />
        </IndustryNameWrapper>

        {hasWritePermission && !(isCreateBlend || isEditBlend) && blend && (
          <EditContainer>
            <ApButtonIcon
              iconName="create"
              onClick={() => {
                if (blend) {
                  onEditIndustryBlend(blend);
                }
              }}
            />
            <ApButtonIcon
              iconName="delete"
              onClick={() => {
                setIsDeleteConfirmationOpen(true);
              }}
            />
          </EditContainer>
        )}
        {blend && !isEditBlend ? (
          <>
            <SelectContainer>
              <SelectYearDescription>
                <InputLabel>Benchmark year</InputLabel>
                <BlendDataText>{blend.year}</BlendDataText>
              </SelectYearDescription>

              <ApTooltip
                enterDelay={0}
                placement="right"
                message={getYearInputHint(Math.max.apply(null, availableYears))}
              >
                <TooltipContent>
                  <ApIcon iconName="help_outline" />
                </TooltipContent>
              </ApTooltip>
            </SelectContainer>

            <SelectContainer>
              <SelectYearDescription>
                <InputLabel>Benchmark metric uplift</InputLabel>
                <BlendDataText>
                  {blend.revenueUpliftPercentage
                    ? blend.revenueUpliftPercentage
                    : 'None'}
                </BlendDataText>
              </SelectYearDescription>

              <ApTooltip
                enterDelay={0}
                placement="right"
                message={REVENUE_UPLIFT_INPUT_HINT}
              >
                <TooltipContent>
                  <ApIcon iconName="help_outline" />
                </TooltipContent>
              </ApTooltip>
            </SelectContainer>
          </>
        ) : null}
      </ConfigurationRow>
      {hasWritePermission && !(isCreateBlend || isEditBlend) ? (
        <IndustryFormActionRowBetween>
          <ApButtonSecondary
            onClick={() => {
              onAddBlend();
            }}
          >
            Create new blend
          </ApButtonSecondary>
          <ApButtonMain
            disabled={!isApplyEnabled}
            onClick={() => {
              updateAndApplyBlend(blend as IIndustryBlend);
            }}
          >
            Apply Industry blend
          </ApButtonMain>
        </IndustryFormActionRowBetween>
      ) : null}
      <DeleteBlendConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        onDelete={() => {
          if (blend) {
            onDeleteBlend(blend as IIndustryBlend);
            setIsDeleteConfirmationOpen(false);
          }
        }}
        onClose={() => {
          setIsDeleteConfirmationOpen(false);
        }}
        blendName={blend?.name || ''}
      />
    </div>
  );
};
